/**
 * User Store
 *
 * Pinia store for user profile and permissions.
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api, API_V1, API_V2 } from '@v2/services/api/client';
import type { User, Permissions, Notification, TeamLeadAccess } from '../types/user';

/**
 * API response types (snake_case from backend)
 */
interface UserMeResponse {
  id: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  emails?: string[];
  phone_numbers?: string[];
  linkedin_id?: string;
  twitter_id?: string;
  my_website?: string;
  my_affiliation?: string;
  affiliation_site?: string;
  avatar_url?: string;
  is_admin?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface TeamLeadAccessResponse {
  teams?: Array<{
    _id: string;
    name: string;
    organizationId?: string;
    role?: string;
  }>;
}

/**
 * User store for managing user profile and permissions
 */
export const useUserStore = defineStore('user', () => {
  // State
  const profile = ref<User | null>(null);
  const permissions = ref<Permissions | null>(null);
  const notifications = ref<Notification[]>([]);
  const loading = ref(false);

  // Getters
  const isAdmin = computed(() => profile.value?.isAdmin ?? false);
  const displayName = computed(() => {
    if (profile.value?.display_name) return profile.value.display_name;
    const first = profile.value?.firstname ?? '';
    const last = profile.value?.lastname ?? '';
    return `${first} ${last}`.trim() || 'User';
  });
  const email = computed(() => profile.value?.emails?.[0] ?? null);
  const hasTeamLeadAccess = computed(() => (permissions.value?.teamLeadAccess?.length ?? 0) > 0);
  const unreadNotificationCount = computed(() => notifications.value.filter(n => !n.read).length);

  /**
   * Load user profile from API
   */
  async function loadProfile(): Promise<void> {
    loading.value = true;
    try {
      const response = await api.get<UserMeResponse>(`${API_V2}/users/me`);
      console.log('[UserStore] Raw API response from /users/me:', response);
      console.log('[UserStore] Response keys:', Object.keys(response));

      profile.value = {
        _id: response.id,
        firstname: response.first_name,
        lastname: response.last_name,
        display_name: response.display_name,
        emails: response.emails,
        phoneNumbers: response.phone_numbers,
        linkedInId: response.linkedin_id,
        twitterId: response.twitter_id,
        myWebsite: response.my_website,
        myAffiliation: response.my_affiliation,
        affiliationSite: response.affiliation_site,
        avatarUrl: response.avatar_url,
        isAdmin: response.is_admin,
        createdAt: response.created_at,
        updatedAt: response.updated_at,
      };

      console.debug('[UserStore] Profile loaded');
      console.log('[UserStore] Profile details:', {
        _id: profile.value._id,
        firstname: profile.value.firstname,
        lastname: profile.value.lastname,
        display_name: profile.value.display_name,
      });
    } catch (error) {
      console.error('[UserStore] Failed to load profile:', error);
      throw error;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Load user permissions (team lead access)
   */
  async function loadPermissions(): Promise<void> {
    try {
      const response = await api.get<TeamLeadAccessResponse>(`${API_V1}/user/teams`);

      const teamLeadAccess: TeamLeadAccess[] = (response.teams ?? []).map(team => ({
        teamId: team._id,
        teamName: team.name,
        organizationId: team.organizationId,
        level: (team.role as TeamLeadAccess['level']) ?? 'member',
      }));

      permissions.value = {
        isAdmin: profile.value?.isAdmin ?? false,
        teamLeadAccess,
        managedOrganizations: [], // Could be populated from a separate endpoint
      };

      console.debug('[UserStore] Permissions loaded');
    } catch (error) {
      console.error('[UserStore] Failed to load permissions:', error);
      // Don't throw - permissions are optional
      permissions.value = {
        isAdmin: profile.value?.isAdmin ?? false,
        teamLeadAccess: [],
        managedOrganizations: [],
      };
    }
  }

  /**
   * Load user notifications
   */
  async function loadNotifications(): Promise<void> {
    try {
      const response = await api.get<Notification[]>(`${API_V1}/user/notifications`);
      notifications.value = response;
      console.debug('[UserStore] Notifications loaded:', response.length);
    } catch (error) {
      console.error('[UserStore] Failed to load notifications:', error);
      // Don't throw - notifications are optional
      notifications.value = [];
    }
  }

  /**
   * Clear all user data (on logout)
   */
  function clear(): void {
    profile.value = null;
    permissions.value = null;
    notifications.value = [];
    loading.value = false;
    console.debug('[UserStore] Cleared');
  }

  /**
   * Update profile locally (after API update)
   */
  function updateProfile(updates: Partial<User>): void {
    if (profile.value) {
      profile.value = { ...profile.value, ...updates };
    }
  }

  /**
   * Mark notification as read
   */
  function markNotificationRead(notificationId: string): void {
    const notification = notifications.value.find(n => n._id === notificationId);
    if (notification) {
      notification.read = true;
    }
  }

  /**
   * Mark all notifications as read
   */
  function markAllNotificationsRead(): void {
    notifications.value.forEach(n => {
      n.read = true;
    });
  }

  return {
    // State
    profile,
    permissions,
    notifications,
    loading,

    // Getters
    isAdmin,
    displayName,
    email,
    hasTeamLeadAccess,
    unreadNotificationCount,

    // Actions
    loadProfile,
    loadPermissions,
    loadNotifications,
    clear,
    updateProfile,
    markNotificationRead,
    markAllNotificationsRead,
  };
});
